/**
 * RichTextEditor — éditeur Lexical avec sync Markdown.
 *
 * Interface publique stable : `value` (Markdown) ↔ `onChange(markdown)`.
 * Le parent ne voit jamais l'arbre Lexical. Le backend continue de
 * recevoir/stocker du Markdown identique à l'ancien <textarea>.
 *
 * Note Étape B : composant minimal sans toolbar.
 *   - L'utilisateur ne peut pas (encore) cliquer sur des boutons.
 *   - En revanche les raccourcis Lexical natifs marchent déjà (Ctrl+B, etc.)
 *     et les transformers Markdown convertissent à la volée la syntaxe tapée
 *     (`**gras**`, `# titre`, `- item`...) en nœuds rich text.
 *
 * La toolbar et les plugins avancés (tableaux, lien modal) seront
 * ajoutés aux Étapes C–E.
 */

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { TRANSFORMERS } from "@lexical/markdown";

import { MarkdownPlugin } from "./plugins/MarkdownPlugin";
import { MarkdownPastePlugin } from "./plugins/MarkdownPastePlugin";
import { TABLE_TRANSFORMER } from "./plugins/tableTransformer";
import { Toolbar } from "./Toolbar";
import "./styles/editor.css";

// Transformers Markdown utilisés pour les raccourcis à la volée
// (ex. taper « ## » + espace → H2). Inclut notre transformer GFM tableau,
// même set que celui utilisé pour la conversion bidirectionnelle.
const SHORTCUT_TRANSFORMERS = [TABLE_TRANSFORMER, ...TRANSFORMERS];

export interface RichTextEditorProps {
  /** Markdown string (état externe). */
  value: string;
  /** Appelé à chaque mutation, avec le Markdown généré. */
  onChange: (markdown: string) => void;
  placeholder?: string;
  /** Pour affichage côté parent du compteur de caractères (non enforcé ici). */
  maxLength?: number;
  readOnly?: boolean;
  /** Hauteur min de la zone éditable (px). */
  minHeight?: number;
  /** Classe CSS additionnelle sur le conteneur racine. */
  className?: string;
}

/**
 * Thème Lexical : map nœud → className CSS.
 * On ne stylise pas tout ici, juste de quoi rendre l'arbre lisible.
 * Les styles applicatifs concrets viennent dans editor.css.
 */
const editorTheme = {
  paragraph: "rte-paragraph",
  heading: {
    h1: "rte-h1",
    h2: "rte-h2",
    h3: "rte-h3",
  },
  list: {
    ul: "rte-ul",
    ol: "rte-ol",
    listitem: "rte-li",
  },
  quote: "rte-quote",
  link: "rte-link",
  text: {
    bold: "rte-bold",
    italic: "rte-italic",
    strikethrough: "rte-strikethrough",
    code: "rte-code-inline",
  },
  code: "rte-code-block",
  table: "rte-table",
  tableCell: "rte-table-cell",
  tableCellHeader: "rte-table-cell-header",
  tableRow: "rte-table-row",
};

export function RichTextEditor({
  value,
  onChange,
  placeholder = "",
  readOnly = false,
  minHeight = 280,
  className,
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: "AmRichTextEditor",
    theme: editorTheme,
    editable: !readOnly,
    onError: (error: Error) => {
      // On ne veut pas crasher l'app si Lexical bronche en prod ;
      // on logge pour debug et on laisse l'éditeur se rétablir.
      // eslint-disable-next-line no-console
      console.error("[RichTextEditor]", error);
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableRowNode,
      TableCellNode,
      HorizontalRuleNode,
    ],
  };

  const containerClass =
    "rte-container" + (readOnly ? " is-readonly" : "") + (className ? " " + className : "");

  return (
    <div className={containerClass} style={{ ["--rte-min-height" as never]: `${minHeight}px` }}>
      <LexicalComposer initialConfig={initialConfig}>
        {!readOnly && <Toolbar />}
        <div className="rte-editor-shell">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="rte-content-editable"
                aria-placeholder={placeholder}
                placeholder={<div className="rte-placeholder">{placeholder}</div>}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <AutoFocusPlugin />
        <ListPlugin />
        <LinkPlugin />
        <TablePlugin hasCellMerge={false} hasCellBackgroundColor={false} />
        {/* Active les raccourcis Markdown à la volée (** ** → gras, # → H1, etc.) */}
        <MarkdownShortcutPlugin transformers={SHORTCUT_TRANSFORMERS} />
        {/* Intercepte les paste pour ignorer le HTML du clipboard
            (sinon coller depuis VS Code crée des CodeNodes). */}
        <MarkdownPastePlugin />
        <MarkdownPlugin value={value} onChange={onChange} />
      </LexicalComposer>
    </div>
  );
}
